/* ──────────────────────────────────────────────────────────────────────────
 * Emergency Response Intelligence — §12.
 *
 * The brief's instruction is a rejection of the obvious implementation:
 *
 *   "When an emergency occurs, don't simply generate an alert."
 *
 * An alert says where. This has to say who goes, by what route, how long it
 * will take given the crowd actually in the way, where the casualty is
 * taken, which hospital has a bed, and which agencies must be brought in —
 * and then stop, because dispatch is a human decision.
 *
 * ── THE TWO THINGS THAT MAKE THIS HARD ──
 *
 * 1. "Nearest" is the wrong answer. The nearest ambulance may be basic life
 *    support when the case is cardiac; the nearest hospital may have no
 *    free bay. This selects the nearest APPROPRIATE responder and a
 *    receiving facility with capacity, and says so when the two differ.
 *
 * 2. Distance is not time. A responder 600 m away across a crowd at
 *    4 persons/m² is further, in the only unit that matters, than one
 *    1.5 km away on a clear road. Crowd density along the route is applied
 *    as a speed penalty, which is the whole reason this engine sits on top
 *    of the crowd model rather than beside it.
 *
 * ── WHAT IT DOES NOT DO ──
 * It does not dispatch. Every plan comes back `requiresAuthorisation: true`
 * and goes through the same incident state machine as everything else. A
 * system that could move an ambulance by itself is not the system the brief
 * describes, and §13 says so explicitly.
 *
 * Everything is SIMULATED.
 * ────────────────────────────────────────────────────────────────────────── */

import { TRUST } from "../data/provenance";
import { ASSEMBLY_AREA_M2 } from "./crowdSafety";
import { buildTwin, AGENCIES } from "../../twin/twin";

/* ── Case types ───────────────────────────────────────────────────────
 * What the case needs decides who is appropriate. Without this the engine
 * can only sort by distance, which is the failure mode the brief is
 * warning about. */
export const CASE_TYPES = {
  cardiac: {
    label: { en: "Cardiac / collapse", mr: "हृदयविकार / बेशुद्धी" },
    needsALS: true, needsTrauma: false, severity: "critical",
    supportingAgencies: ["HEALTH", "POLICE"],
    goldenMinutes: 8,
  },
  crush: {
    label: { en: "Crush injury", mr: "चेंगराचेंगरी दुखापत" },
    needsALS: true, needsTrauma: true, severity: "critical",
    supportingAgencies: ["HEALTH", "POLICE", "NDRF", "FIRE"],
    goldenMinutes: 10,
  },
  heat: {
    label: { en: "Heat exhaustion", mr: "उष्माघात" },
    needsALS: false, needsTrauma: false, severity: "urgent",
    supportingAgencies: ["HEALTH"],
    goldenMinutes: 20,
  },
  drowning: {
    label: { en: "Drowning / water rescue", mr: "बुडणे / जल बचाव" },
    needsALS: true, needsTrauma: false, severity: "critical",
    supportingAgencies: ["NDRF", "HEALTH", "FIRE"],
    goldenMinutes: 6,
  },
  fire: {
    label: { en: "Fire", mr: "आग" },
    needsALS: false, needsTrauma: true, severity: "critical",
    supportingAgencies: ["FIRE", "POLICE", "HEALTH", "NDRF"],
    goldenMinutes: 8,
  },
  trauma: {
    label: { en: "Trauma / fall", mr: "अपघात / पडणे" },
    needsALS: false, needsTrauma: true, severity: "urgent",
    supportingAgencies: ["HEALTH", "POLICE"],
    goldenMinutes: 15,
  },
};

/* ── Movement model ────────────────────────────────────────────────────
 * Base speeds on a clear route, km/h. An emergency vehicle in a pilgrim
 * theatre is not doing highway speeds even with nothing in the way. */
export const BASE_SPEED_KMH = { ambulance: 34, officer: 20, fire: 30 };

/* Crowd penalty. Above roughly 2 persons/m² a vehicle cannot make useful
 * progress, and beyond 4 it is effectively walking pace behind a wedge of
 * officers. Modelled as a multiplier on free speed. */
export function crowdSpeedFactor(density) {
  if (density == null) return null;          // unknown, not "clear"
  if (density <= 0.8) return 1;
  if (density >= 5) return 0.08;
  return Math.max(0.08, 1 - (density - 0.8) * 0.22);
}

const R_EARTH_KM = 6371;
export function haversineKm(a, b) {
  if (!a || !b) return null;
  const toRad = (d) => (d * Math.PI) / 180;
  const [lon1, lat1] = a;
  const [lon2, lat2] = b;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R_EARTH_KM * Math.asin(Math.sqrt(h));
}

function sectorDensity(liveState, sectorId) {
  const s = liveState?.sectors?.find((x) => x.id === sectorId);
  const area = ASSEMBLY_AREA_M2[sectorId];
  if (!s || !area) return null;
  return s.current / area;
}

/* Travel time accounting for the crowd actually in the way. Where density
 * is unknown the time is returned as null with a reason, never as though
 * the route were clear. */
function travelMinutes(distanceKm, kind, density) {
  if (distanceKm == null) return { minutes: null, reason: "no position for this unit" };
  const factor = crowdSpeedFactor(density);
  if (factor == null) {
    return { minutes: null, reason: "crowd density unknown along this route" };
  }
  const speed = (BASE_SPEED_KMH[kind] ?? 20) * factor;
  return { minutes: Math.round((distanceKm / speed) * 60), factor, speed: Number(speed.toFixed(1)) };
}

/* ── Responder selection ───────────────────────────────────────────────── */
export function rankResponders(incident, twin, liveState) {
  const caseType = CASE_TYPES[incident.caseType] ?? CASE_TYPES.trauma;
  const wantsMedical = caseType.needsALS || incident.caseType !== "fire";

  const pool = [
    ...(wantsMedical ? twin.byType("ambulance") : []),
    ...twin.byType("officer"),
  ];

  return pool
    .map((unit) => {
      const kind = unit.type === "ambulance" ? "ambulance"
        : unit.agency?.id === "FIRE" ? "fire" : "officer";
      const distanceKm = haversineKm(unit.coord, incident.coord);
      /* Density is taken from the destination sector — the crowd that will
       * actually obstruct the final approach, which is where emergency
       * vehicles genuinely lose time. */
      const density = sectorDensity(liveState, incident.sector);
      const t = travelMinutes(distanceKm, kind, density);

      /* Appropriateness is separate from proximity and is never traded
       * against it silently. */
      const appropriate =
        unit.type === "ambulance"
          ? (!caseType.needsALS || unit.advancedLifeSupport === true)
          : caseType.supportingAgencies.includes(unit.agency?.id);

      return {
        id: unit.id,
        name: unit.name,
        type: unit.type,
        agency: unit.agency,
        status: unit.status,
        advancedLifeSupport: unit.advancedLifeSupport ?? null,
        distanceKm: distanceKm == null ? null : Number(distanceKm.toFixed(2)),
        etaMinutes: t.minutes,
        etaUnavailableReason: t.reason ?? null,
        crowdSpeedFactor: t.factor ?? null,
        effectiveSpeedKmh: t.speed ?? null,
        appropriate,
        available: unit.status === "available",
      };
    })
    /* Appropriate and available first, then by ETA. A unit with no ETA
     * sorts last rather than first — an unknown must never win a race. */
    .sort((a, b) => {
      const score = (r) => (r.appropriate && r.available ? 0 : r.available ? 1 : 2);
      const s = score(a) - score(b);
      if (s !== 0) return s;
      if (a.etaMinutes == null) return 1;
      if (b.etaMinutes == null) return -1;
      return a.etaMinutes - b.etaMinutes;
    });
}

/* ── Receiving facility ────────────────────────────────────────────────
 * "Nearest hospital" is the wrong answer if it has no free bay. */
export function rankReceivingFacilities(incident, twin, liveState) {
  const caseType = CASE_TYPES[incident.caseType] ?? CASE_TYPES.trauma;
  const facilities = [...twin.byType("hospital"), ...twin.byType("medicalCamp")];

  return facilities
    .map((f) => {
      const distanceKm = haversineKm(incident.coord, f.coord);
      const density = sectorDensity(liveState, f.sector);
      const t = travelMinutes(distanceKm, "ambulance", density);
      const st = f.state ?? null;

      return {
        id: f.id,
        name: f.name,
        type: f.type,
        distanceKm: distanceKm == null ? null : Number(distanceKm.toFixed(2)),
        transferMinutes: t.minutes,
        /* Null, not zero, when capacity is unknown. A facility whose state
         * nobody reported must not look empty and therefore attractive. */
        bedsFree: st ? st.bedsFree : null,
        emergencyFree: st ? st.emergencyFree : null,
        trauma: st ? st.trauma === true : null,
        hasCapacity: st ? st.emergencyFree > 0 : null,
        suitable: st
          ? st.emergencyFree > 0 && (!caseType.needsTrauma || st.trauma === true)
          : null,
      };
    })
    .sort((a, b) => {
      const score = (f) => (f.suitable === true ? 0 : f.suitable === null ? 1 : 2);
      const s = score(a) - score(b);
      if (s !== 0) return s;
      if (a.transferMinutes == null) return 1;
      if (b.transferMinutes == null) return -1;
      return a.transferMinutes - b.transferMinutes;
    });
}

/* ── Emergency corridor ────────────────────────────────────────────────
 * The alternative route the brief asks for. Emergency routes are entities
 * in the twin; this picks the one that starts nearest the incident and
 * reports whether the crowd makes it usable. */
export function emergencyCorridor(incident, twin, liveState) {
  const routes = twin.byType("emergencyRoute");
  if (!routes.length) {
    return { available: false, reason: "No emergency route is mapped in the twin." };
  }
  const scored = routes
    .map((r) => {
      const head = r.coords?.[0] ?? r.coord;
      const distanceKm = haversineKm(incident.coord, head);
      const density = sectorDensity(liveState, r.sector ?? incident.sector);
      const factor = crowdSpeedFactor(density);
      return {
        id: r.id,
        name: r.name,
        distanceToHeadKm: distanceKm == null ? null : Number(distanceKm.toFixed(2)),
        crowdSpeedFactor: factor,
        usable: factor == null ? null : factor > 0.25,
        caveat: r.provenance?.note ?? null,
      };
    })
    .sort((a, b) => (a.distanceToHeadKm ?? 999) - (b.distanceToHeadKm ?? 999));

  return { available: true, routes: scored, nearest: scored[0] };
}

/* ── The plan ──────────────────────────────────────────────────────────── */
export function buildResponsePlan(incident, liveState, { twin = null } = {}) {
  const t = twin ?? buildTwin(liveState);
  const caseType = CASE_TYPES[incident.caseType] ?? CASE_TYPES.trauma;

  const responders = rankResponders(incident, t, liveState);
  const facilities = rankReceivingFacilities(incident, t, liveState);
  const corridor = emergencyCorridor(incident, t, liveState);

  const primary = responders.find((r) => r.appropriate && r.available) ?? null;
  const nearestAny = responders
    .filter((r) => r.etaMinutes != null)
    .sort((a, b) => a.etaMinutes - b.etaMinutes)[0] ?? null;
  const receiving = facilities.find((f) => f.suitable === true) ?? null;

  /* The honest headline: does the plan meet the clinical window, and if
   * not, say by how much rather than presenting the ETA as a success. */
  const withinGoldenWindow = primary?.etaMinutes == null
    ? null
    : primary.etaMinutes <= caseType.goldenMinutes;

  const warnings = [];
  if (!primary) {
    warnings.push("No appropriate and available responder was found. Escalate to the command centre for mutual aid.");
  }
  if (primary && nearestAny && nearestAny.id !== primary.id) {
    warnings.push(
      `The nearest unit (${nearestAny.name?.en ?? nearestAny.id}, ETA ${nearestAny.etaMinutes} min) is not appropriate for a ${caseType.label.en.toLowerCase()} case. ${primary.name?.en ?? primary.id} is recommended instead at ETA ${primary.etaMinutes} min.`,
    );
  }
  if (withinGoldenWindow === false) {
    warnings.push(
      `Projected response of ${primary.etaMinutes} min exceeds the ${caseType.goldenMinutes} min clinical window for this case type.`,
    );
  }
  if (!receiving) {
    warnings.push("No receiving facility with free emergency capacity and the required capability was found.");
  }
  if (primary?.crowdSpeedFactor != null && primary.crowdSpeedFactor < 0.3) {
    warnings.push(
      `Crowd density is reducing approach speed to ${Math.round(primary.crowdSpeedFactor * 100)}% of free-running. A cleared corridor or a foot team may beat the vehicle.`,
    );
  }

  return {
    incident: { ...incident, caseLabel: caseType.label, severity: caseType.severity },
    severity: caseType.severity,
    primaryResponder: primary,
    nearestResponder: nearestAny,
    alternates: responders.filter((r) => r.id !== primary?.id).slice(0, 4),
    receivingFacility: receiving,
    facilityOptions: facilities.slice(0, 4),
    emergencyCorridor: corridor,
    estimatedResponseMinutes: primary?.etaMinutes ?? null,
    goldenWindowMinutes: caseType.goldenMinutes,
    withinGoldenWindow,
    supportingAgencies: caseType.supportingAgencies.map((a) => AGENCIES[a]).filter(Boolean),
    warnings,
    /* §12 ends at "Command authorization". The engine proposes; it never
     * dispatches. */
    requiresAuthorisation: true,
    authorisationNote:
      "This is a decision-support proposal. Dispatch requires an authorised officer's approval and is recorded through the incident workflow.",
    trust: TRUST.SIMULATED,
  };
}

export const RESPONSE_ASSUMPTIONS = [
  "Unit positions, hospital bed counts and ambulance capability flags are invented for the demo. A real deployment reads them from the 108 dispatch system and the hospital bed registry.",
  "Travel time is straight-line distance at a base speed reduced by crowd density in the destination sector. It is not road-network routed and does not account for one-ways, barricades or bridge closures.",
  "Crowd speed penalty is a stated curve, not a measured one: free speed up to 0.8 persons/m², falling to 8% at 5 persons/m². It is the reason a nearer unit can be the slower one.",
  "Clinical golden windows are planning figures for triage ordering, not medical advice.",
  "Where density or a unit position is unknown, the ETA is null and the unit sorts last. An unknown never wins a race against a measured figure.",
];
